import type { LanguageCode } from '@graphql/core/schema';

interface Language {
  /** Display name of the language translated to the given language */
  name: string;
  /** Country code in ISO 3166-1 alpha-2 format */
  countryCode: string;
}

/**
 * Available language codes in ISO 639-1 format
 */
export type LanguageCodes = LanguageCode;

/**
 * Available language codes in ISO 639-1 format with `fallback` option
 */
export type LanguageCodesWithFallback = LanguageCode | 'fallback';

/**
 * Mapping of languages by the key (in ISO 639-1 format)
 */
export type LanguageConfig = {
  [K in LanguageCode]: Language;
};

export const languages: LanguageConfig = {
  en: { name: 'English', countryCode: 'gb' },
  nb: { name: 'Norsk bokmål', countryCode: 'no' },
  nn: { name: 'Norsk nynorsk', countryCode: 'no' },
  se: { name: 'Davvisámegiella', countryCode: 'sami' },
  sv: { name: 'Svenska', countryCode: 'se' },
  pl: { name: 'Polski', countryCode: 'pl' },
  fi: { name: 'Suomi', countryCode: 'fi' },
  fr: { name: 'Français', countryCode: 'fr' },
  de: { name: 'Deutsch', countryCode: 'de' },
  da: { name: 'Dansk', countryCode: 'dk' },
  lv: { name: 'Latviešu', countryCode: 'lv' },
  lt: { name: 'Lietuvių k.', countryCode: 'lt' },
  pt: { name: 'Português', countryCode: 'pt' },
  es: { name: 'Español', countryCode: 'es' },
  hr: { name: 'Hrvatski', countryCode: 'hr' },
  it: { name: 'Italiano', countryCode: 'it' },
  sq: { name: 'Shqip', countryCode: 'al' },
  tr: { name: 'Türkçe', countryCode: 'tr' },
  cs: { name: 'Česká republika', countryCode: 'cz' },
  hu: { name: 'Magyarország', countryCode: 'hu' },
  ro: { name: 'România', countryCode: 'ro' },
  ru: { name: 'Российская Федерация', countryCode: 'ru' },
  sr: { name: 'Република Србија', countryCode: 'rs' },
  nl: { name: 'Nederlands', countryCode: 'nl' },
};

export interface ExpandedLanguage extends Language {
  /** Language code in ISO 639-1 format */
  languageCode: LanguageCode;
}

/**
 * Gets details about a specific language by the language code in ISO 639-1 format
 */
export function getLanguage(languageCode: LanguageCode): ExpandedLanguage;
export function getLanguage(languageCode: LanguageCode | 'fallback'): ExpandedLanguage | undefined;
export function getLanguage(languageCode: LanguageCode, withoutFallback: true): ExpandedLanguage | undefined;
export function getLanguage(languageCode: 'fallback', withoutFallback: true): undefined;
export function getLanguage(
  languageCode: LanguageCode | 'fallback',
  withoutFallback?: boolean
): ExpandedLanguage | undefined {
  if (!languages[languageCode]) {
    if (withoutFallback) {
      return undefined;
    }
    return {
      ...languages.en,
      languageCode: 'en',
    };
  }

  return {
    ...languages[languageCode],
    languageCode,
  };
}
